import type { I18nInstance } from '@affine/i18n';
import { isQuotaExceededError } from '@affine/nbstore';
import type { DatabaseLimitError } from '@streamlain/database-identity';

import { IMPORT_LIMIT_CATALOG } from './import-limit-catalog';

export type ImportLimitDetails =
  | {
      kind:
        | 'record-bytes'
        | 'columns'
        | 'root-index-bytes'
        | 'rows'
        | 'empty'
        /** Excel file and expanded workbook size, both in MiB. */
        | 'excel-file-size'
        | 'excel-expanded-size';
      actual: number;
      limit: number;
    }
  | { kind: 'row-bytes'; actual: number; limit: number; row: number }
  | {
      kind: 'cell';
      actual: number;
      limit: number;
      row: number;
      column: string;
    };

const IMPORT_LIMIT_KINDS: readonly string[] = Object.keys(IMPORT_LIMIT_CATALOG);

/** OpConsumer already transports Error.data; no text parsing or new RPC path. */
export class ImportLimitError extends Error {
  readonly code = 'DATABASE_IMPORT_LIMIT';
  constructor(readonly data: ImportLimitDetails) {
    super(`Database import ${data.kind}: ${data.actual} > ${data.limit}.`);
    this.name = 'ImportLimitError';
  }
}

/**
 * The import preflight's database limit as import details; `row` is the
 * 1-based data row of the source file and `column` the column name.
 * File imports only create character-limited cell types.
 */
export function importLimitFromDatabaseLimit(
  error: DatabaseLimitError,
  columns: readonly { id: string; name: string }[]
): ImportLimitDetails | undefined {
  const { unit, actual, limit, rowIndex, columnId } = error.detail;
  switch (unit) {
    case 'rows':
    case 'columns':
      return { kind: unit, actual, limit };
    case 'bytes':
      return rowIndex === undefined
        ? undefined
        : { kind: 'row-bytes', actual, limit, row: rowIndex };
    case 'chars': {
      const column = columns.find(column => column.id === columnId)?.name;
      return rowIndex === undefined || column === undefined
        ? undefined
        : { kind: 'cell', actual, limit, row: rowIndex, column };
    }
    default:
      return undefined;
  }
}

function positiveInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

export function readImportLimitDetails(
  error: unknown
): ImportLimitDetails | undefined {
  if (
    !error ||
    typeof error !== 'object' ||
    !('code' in error) ||
    error.code !== 'DATABASE_IMPORT_LIMIT' ||
    !('data' in error)
  )
    return;
  const data = error.data;
  if (
    !data ||
    typeof data !== 'object' ||
    !('kind' in data) ||
    !('actual' in data) ||
    !('limit' in data)
  )
    return;
  if (
    !IMPORT_LIMIT_KINDS.some(kind => kind === data.kind) ||
    !Number.isSafeInteger(data.actual) ||
    Number(data.actual) < 0 ||
    !positiveInteger(data.limit)
  )
    return;
  if (
    (data.kind === 'row-bytes' || data.kind === 'cell') &&
    !('row' in data && positiveInteger(data.row))
  )
    return;
  if (
    data.kind === 'cell' &&
    !('column' in data && typeof data.column === 'string')
  )
    return;
  return data as ImportLimitDetails;
}

export function formatImportLimit(
  t: Pick<I18nInstance, 't' | 'language'>,
  details: ImportLimitDetails
): string {
  const numbers = new Intl.NumberFormat(t.language);
  return t.t(`com.streamlain.import.preview.limit.${details.kind}`, {
    actual: numbers.format(details.actual),
    limit: numbers.format(details.limit),
    ...('row' in details ? { row: numbers.format(details.row) } : {}),
    ...('column' in details ? { column: details.column } : {}),
  });
}

/**
 * Local storage ran full during an import. Committed row batches stay saved;
 * every later row of the source was not stored. Undefined for other errors.
 */
export function formatImportStorageFull(
  t: Pick<I18nInstance, 't' | 'language'>,
  error: unknown,
  savedRows: number
): string | undefined {
  if (!isQuotaExceededError(error)) return;
  return t.t('com.streamlain.import.status.failed.message.local-storage-full', {
    saved: new Intl.NumberFormat(t.language).format(savedRows),
  });
}

/**
 * The user cancelled an import. Committed row batches stay saved, so the
 * message names them; without a saved row it is the plain cancel message.
 * Undefined for other errors.
 */
export function formatImportCancelled(
  t: Pick<I18nInstance, 't' | 'language'>,
  error: unknown,
  savedRows: number
): string | undefined {
  if (!(error instanceof Error) || error.name !== 'AbortError') return;
  if (savedRows <= 0)
    return t.t('com.streamlain.import.status.failed.message.cancelled');
  return t.t('com.streamlain.import.status.failed.message.cancelled-saved', {
    saved: new Intl.NumberFormat(t.language).format(savedRows),
  });
}
